import React, { useState } from "react";
import axios from "axios";
import { FileText, Download, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { SectionHeading, Reveal } from "../common/Section";
import { generateBoardPack } from "../../lib/boardPack";
import { track, EVENTS } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OUTLINE = ["AI risk landscape", "Governance architecture", "Human accountability", "Policy controls", "Data handling", "Deployment options", "Regulatory mapping", "Incident management", "Audit & evidence model", "Implementation process", "Questions boards should ask"];

export default function BoardPack() {
  const [form, setForm] = useState({ name: "", company: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const [errors, setErrors] = useState({});
  const set = (k, v) => { setForm({ ...form, [k]: v }); setErrors({ ...errors, [k]: undefined }); };

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.name.trim()) err.name = "Please enter your full name.";
    if (!form.company.trim()) err.company = "Please enter your company.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Please enter a valid business email.";
    setErrors(err);
    if (Object.keys(err).length) return;
    setBusy(true);
    track(EVENTS.BOARDPACK_REQUESTED, { company: form.company });
    try {
      await axios.post(`${API}/board-pack`, form).catch(() => {});
      await generateBoardPack({ name: form.name, company: form.company });
      setDone(true);
    } catch (e2) {
      setErrors({ form: "Could not generate the Board Pack. Please try again or email info@norse3.com." });
    } finally { setBusy(false); }
  };

  return (
    <section className="border-t border-slate-200 py-16 lg:py-24 bg-white">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="rounded-xl border border-navy-800 bg-gradient-to-br from-navy-900 to-navy-950 p-8 lg:p-12 shadow-xl">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <SectionHeading eyebrow="Board pack" title="Building the business case for controlled enterprise AI" subtitle="A concise, professionally structured pack for leadership — the risks, the governance architecture and the questions a board should ask. Educational only, not legal advice." />
              <ul className="mt-6 grid sm:grid-cols-2 gap-2">
                {OUTLINE.map((o) => (
                  <li key={o} className="flex items-start gap-2 text-sm text-muted-foreground"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {o}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={120}>
              <div className="rounded-lg border border-white/10 bg-navy-900/70 p-6">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><FileText className="h-5 w-5" /></span>
                  <p className="text-sm font-semibold text-foreground">Send me the Board Pack</p>
                </div>
                {done ? (
                  <div className="mt-5 text-center py-6" data-testid="boardpack-done">
                    <CheckCircle2 className="mx-auto h-8 w-8 text-signal-green" />
                    <p className="mt-3 text-sm text-foreground">Your Board Pack has downloaded. Check your downloads folder.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} noValidate className="mt-5 space-y-3" data-testid="boardpack-form">
                    <div><Input placeholder="Full name" aria-label="Full name" aria-invalid={!!errors.name} value={form.name} onChange={(e) => set("name", e.target.value)} className={`bg-navy-800 ${errors.name ? "border-signal-red" : "border-white/12"}`} data-testid="boardpack-name" />{errors.name && <p className="mt-1 text-xs text-signal-red" data-testid="boardpack-name-error">{errors.name}</p>}</div>
                    <div><Input placeholder="Company" aria-label="Company" aria-invalid={!!errors.company} value={form.company} onChange={(e) => set("company", e.target.value)} className={`bg-navy-800 ${errors.company ? "border-signal-red" : "border-white/12"}`} data-testid="boardpack-company" />{errors.company && <p className="mt-1 text-xs text-signal-red" data-testid="boardpack-company-error">{errors.company}</p>}</div>
                    <div><Input type="email" placeholder="Business email" aria-label="Business email" aria-invalid={!!errors.email} value={form.email} onChange={(e) => set("email", e.target.value)} className={`bg-navy-800 ${errors.email ? "border-signal-red" : "border-white/12"}`} data-testid="boardpack-email" />{errors.email && <p className="mt-1 text-xs text-signal-red" data-testid="boardpack-email-error">{errors.email}</p>}</div>
                    <Button type="submit" disabled={busy} data-testid="boardpack-submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                      {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Send me the Board Pack
                    </Button>
                    {errors.form && <p className="text-xs text-signal-red" data-testid="boardpack-form-error">{errors.form}</p>}
                    <p className="text-xs text-muted-foreground/70">We'll email you occasionally about norse3. No spam.</p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
